import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import {
  StatusChip,
  StatusChipProps,
  StatusChipVariant,
} from '@/components/legacy-ui/StatusChip';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { Risk, RiskStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';
import {
  getStatusLabel,
  type VulnerabilityStatusMode,
} from '@/utils/vulnerabilityStatus.util';

export function VulnerabilityStatusComponent(props: {
  vulnerability: Risk;
  className?: string;
}) {
  const { vulnerability, className } = props;
  const { mode } = useVulnerabilityStatusMode();

  const vulnerabilityStatusProps = getVulnerabilityStatusProps(
    vulnerability,
    mode
  );

  if (!vulnerabilityStatusProps) return null;

  return <StatusChip {...vulnerabilityStatusProps} className={cn(className)} />;
}

interface VulnerabilityStatusChipProps extends StatusChipProps {
  variant: StatusChipVariant;
}

// eslint-disable-next-line react-refresh/only-export-components
export const getVulnerabilityStatusProps = (
  vulnerability: Risk,
  mode: VulnerabilityStatusMode
): VulnerabilityStatusChipProps | null => {
  if (!vulnerability?.status) return null;

  const { status, statusLabel } = getRiskStatusLabel(vulnerability.status);

  switch (status) {
    case RiskStatus.Triaged:
      return {
        label: getStatusLabel(RiskStatus.Triaged, mode),
        variant: getVariantForStatus('triaged', vulnerabilityStatusVariants),
        icon: <ClockIcon className="size-3 shrink-0" />,
        tooltip: `Vulnerability is ${mode === 'legacy' ? 'pending review' : 'detected and under analysis'}`,
      };

    case RiskStatus.Opened:
      return {
        label: getStatusLabel(RiskStatus.Opened, mode),
        variant: getVariantForStatus('opened', vulnerabilityStatusVariants),
        icon: <ExclamationTriangleIcon className="size-3 shrink-0" />,
        tooltip: `Vulnerability is ${mode === 'legacy' ? 'open and needs attention' : 'demonstrated and requires action'}`,
      };

    case RiskStatus.Remediated:
      return {
        label: getStatusLabel(RiskStatus.Remediated, mode),
        variant: getVariantForStatus('remediated', vulnerabilityStatusVariants),
        icon: <CheckCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability has been remediated',
      };

    case RiskStatus.Accepted:
      return {
        label: getStatusLabel(RiskStatus.Accepted, mode),
        variant: getVariantForStatus('accepted', vulnerabilityStatusVariants),
        icon: <NoSymbolIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability risk has been accepted',
      };

    case RiskStatus.Deleted:
      return {
        label: getStatusLabel(RiskStatus.Deleted, mode),
        variant: getVariantForStatus('deleted', vulnerabilityStatusVariants),
        icon: <XCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability has been rejected',
      };

    default:
      return {
        label: statusLabel || 'Unknown',
        variant: 'muted',
        icon: null,
        tooltip: `Status: ${statusLabel || 'Unknown'}`,
      };
  }
};
